"use client";

import { useState } from "react";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
  Select,
  Switch,
  Autocomplete,
} from "@/ui";
import TextField from "@components/shared/form/TextField";
import FormSection from "./parts/FormSection";
import FormFieldTitle from "./parts/FormFieldTitle";
import { TypeOfWork } from "@lib/enums";
import isEmailValid from "@lib/helpers/isEmailValid";
import ErrorSummary from "./parts/ErrorSummary";
import isPlaceholderEmail from "@lib/candidates/isPlaceholderEmail";
import useCandidateSourcedFrom from "@lib/candidates/useCandidateSourcedFrom";

const TYPE_OF_WORK_OPTIONS = Object.values(TypeOfWork).map((value) => ({
  value,
  label: value,
}));

export default function EditPersonalDetails({ onClose, open, candidate, onUpdate }) {
  const [details, setDetails] = useState({
    name: candidate?.name || "",
    position_title: candidate?.position_title || "",
    address_address: candidate?.address_address || "",
    email: candidate?.email || "",
    phone: candidate?.phone || "",
    available_now: candidate?.available_now || false,
    type_of_work: candidate?.type_of_work || "",
    salary: candidate?.salary || "",
    languages_spoken: candidate?.languages_spoken || "",
    website: candidate?.website || "",
    linkedin: candidate?.linkedin || "",
    github: candidate?.github || "",
    sourced_from: candidate?.sourced_from || "",
  });
  const [formError, setFormError] = useState({});
  const { sourcedFrom, isLoading: sourcedFromIsLoading } = useCandidateSourcedFrom();

  const showPlaceholderEmailHelp = isPlaceholderEmail(details.email);

  const validateEmail = (email) => {
    const updatedError = { ...formError };
    if (email && isEmailValid(email.trim())) {
      delete updatedError.email;
      setFormError(updatedError);
      return;
    }
    setFormError({ ...formError, email: ["Please enter a valid email address"] });
  };

  const validateRequired = (name, value) => {
    const updatedError = { ...formError };
    if (value?.length > 0) {
      delete updatedError[name];
      setFormError(updatedError);
      return;
    }
    setFormError({ ...formError, [name]: [`Please enter a value for ${name.replace("_", " ")}`] });
  };

  const handleChange = (event) => {
    const targetId = event.target.name;
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setDetails({ ...details, [targetId]: value });
  };

  // The /src/ui Select is a base-ui component: it hands back the raw value
  // rather than a change event.
  const handleSelectChange = (field) => (value) => {
    setDetails({ ...details, [field]: value });
  };

  const handleSubmit = () => {
    onUpdate({ ...candidate, ...details });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit personal details</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            <ErrorSummary formError={formError} />
            <FormSection>
              <FormFieldTitle>Sourced from:</FormFieldTitle>
              <Autocomplete
                items={sourcedFrom}
                getOptionLabel={(value) => value}
                getOptionKey={(value) => value}
                placeholder="e.g. Introduction from Jane, Conference, LinkedIn..."
                inputValue={details.sourced_from}
                onInputChange={(value) => setDetails({ ...details, sourced_from: value })}
                onSelect={(value) => setDetails({ ...details, sourced_from: value })}
                clearOnSelect={false}
                loading={sourcedFromIsLoading}
                loadingText="Loading…"
                emptyText="No matches — your typed value will be saved"
              />
            </FormSection>
            <FormSection>
              <FormFieldTitle required>Name:</FormFieldTitle>
              <TextField
                fullWidth name="name" variant="outlined"
                isError={!!formError.name}
                onBlur={() => validateRequired("name", details.name)}
                value={details.name} onChange={handleChange}
              />
            </FormSection>
            <FormSection>
              <FormFieldTitle required>Position:</FormFieldTitle>
              <TextField
                fullWidth name="position_title" variant="outlined"
                isError={!!formError.position_title}
                onBlur={() => validateRequired("position_title", details.position_title)}
                value={details.position_title} onChange={handleChange}
              />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Location:</FormFieldTitle>
              <TextField fullWidth name="address_address" variant="outlined" value={details.address_address} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Email address:</FormFieldTitle>
              <TextField
                fullWidth name="email" type="email" variant="outlined"
                value={details.email}
                isError={Object.hasOwn(formError, "email")}
                onBlur={(event) => validateEmail(event.target.value)}
                onChange={handleChange}
                helperText={showPlaceholderEmailHelp ? "This is a placeholder email. Replace it with the correct address." : ""}
              />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Phone:</FormFieldTitle>
              <TextField fullWidth name="phone" type="text" variant="outlined" value={details.phone} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Available for hire:</FormFieldTitle>
              <Switch
                name="available_now"
                checked={details.available_now}
                onChange={(checked) => setDetails({ ...details, available_now: checked })}
                label="Yes"
              />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Type of work:</FormFieldTitle>
              <Select
                name="type_of_work"
                value={Object.values(TypeOfWork).includes(details.type_of_work) ? details.type_of_work : ""}
                onChange={handleSelectChange("type_of_work")}
                options={TYPE_OF_WORK_OPTIONS}
                placeholder="- Select type of work -"
              />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Salary expectations:</FormFieldTitle>
              <TextField fullWidth name="salary" variant="outlined" value={details.salary} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Languages:</FormFieldTitle>
              <TextField fullWidth name="languages_spoken" variant="outlined" value={details.languages_spoken} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Personal website:</FormFieldTitle>
              <TextField fullWidth name="website" variant="outlined" value={details.website} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>LinkedIn profile:</FormFieldTitle>
              <TextField fullWidth name="linkedin" variant="outlined" value={details.linkedin} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>GitHub profile:</FormFieldTitle>
              <TextField fullWidth name="github" variant="outlined" value={details.github} onChange={handleChange} />
            </FormSection>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
